/**
 * Fuente del catálogo para el sitio público.
 *
 * Diseño deliberado: el estado inicial es el **snapshot** que viene bundleado en
 * `data/site.js`, y el API lo reemplaza cuando responde.
 *
 * Por qué así y no con un skeleton de carga:
 *  - el sitio nunca se ve vacío ni salta de layout;
 *  - si el API o la D1 se caen, la landing **sigue mostrando catálogo** en vez de un
 *    hueco. Es el "fallback" que pedía el plan, sin código extra;
 *  - el primer render no espera a la red.
 *
 * Mientras un producto no tenga foto propia (`img: null`), se usa la imagen que ya
 * traía el snapshot para ese mismo id. Así los 8 originales se siguen viendo igual
 * hasta que la dueña suba las fotos reales.
 */

import { useEffect, useState } from 'react';
import { PRODUCTS } from '@/data/site';

/**
 * Ajustes de respaldo si el API no responde.
 *
 * Son los MISMOS valores que el default de `functions/api/_ajustes.js` y los de la
 * migración. Están duplicados porque el bundle del navegador no puede importar de
 * `functions/` — **el que manda es el del servidor**; este sólo evita que un API
 * caído deje al sitio sin promoción y con el carrito mudo.
 */
export const AJUSTES_RESPALDO = Object.freeze({
  payLinksEnabled: true,
  promoEnabled: true,
  promoTiers: Object.freeze([
    Object.freeze({ minUnits: 3, percent: 5 }),
    Object.freeze({ minUnits: 6, percent: 10 }),
  ]),
});

/** Imagen del snapshot por id, para rellenar los que aún no tienen foto propia. */
const IMG_SNAPSHOT = Object.fromEntries(PRODUCTS.map((p) => [p.id, p.img]));

function normalizar(api) {
  return api
    .map((p) => ({
      id: p.id,
      brand: p.brand,
      name: p.name,
      note: p.note,
      gender: p.gender,
      // El snapshot bundleado no tiene categoría: si el API se cae, los filtros de
      // categoría simplemente no se pintan y queda el catálogo entero, como antes.
      category: p.category || null,
      img: p.img || IMG_SNAPSHOT[p.id] || null,
      imgW: p.imgW ?? null,
      imgH: p.imgH ?? null,
      // El snapshot bundleado no tiene precio ni inventario: si el API se cae, la
      // card vuelve sola a "Consultar por WhatsApp", que es exactamente el estado
      // anterior a este cambio. No hay un modo intermedio raro.
      priceCents: p.priceCents ?? null,
      // El snapshot bundleado no tiene precio tachado: si el API se cae, las cards
      // vuelven a mostrar un solo precio, que es el estado anterior a este cambio.
      compareAtCents: p.compareAtCents ?? null,
      agotado: p.agotado === true,
      payUrl: p.payUrl || null,
    }))
    // Sin imagen no se puede renderizar la card: mejor omitir el producto que
    // dejar un recuadro roto en un sitio de lujo.
    .filter((p) => p.img);
}

/** Normaliza los ajustes que manda el API. Un campo con forma rara cae al
 *  respaldo en vez de propagar `undefined` a la calculadora del carrito. */
function normalizarAjustes(api) {
  const respaldo = { ...AJUSTES_RESPALDO, promoTiers: [...AJUSTES_RESPALDO.promoTiers] };
  if (!api || typeof api !== 'object') return respaldo;
  const bool = (v, def) => (typeof v === 'boolean' ? v : def);

  // Los tramos se aceptan como vengan y se ordenan acá. Si el API todavía manda el
  // par suelto (bundle nuevo contra Function vieja, durante un deploy), se arma un
  // tramo con eso en vez de quedarse sin promoción.
  let tramos = Array.isArray(api.promoTiers)
    ? api.promoTiers
        .filter((t) => t && Number.isInteger(t.minUnits) && Number.isInteger(t.percent) && t.percent > 0)
        .map((t) => ({ minUnits: t.minUnits, percent: t.percent }))
        .sort((a, b) => a.minUnits - b.minUnits)
    : [];
  if (!tramos.length && Number.isInteger(api.promoMinUnits) && Number.isInteger(api.promoPercent)) {
    tramos = [{ minUnits: api.promoMinUnits, percent: api.promoPercent }];
  }

  return {
    payLinksEnabled: bool(api.payLinksEnabled, AJUSTES_RESPALDO.payLinksEnabled),
    promoEnabled: bool(api.promoEnabled, AJUSTES_RESPALDO.promoEnabled),
    promoTiers: tramos.length ? tramos : respaldo.promoTiers,
  };
}

export function useCatalogo() {
  const [productos, setProductos] = useState(PRODUCTS);
  const [ajustes, setAjustes] = useState(AJUSTES_RESPALDO);
  const [origen, setOrigen] = useState('snapshot');

  useEffect(() => {
    let vivo = true;
    const ctrl = new AbortController();
    // Si el API tarda demasiado, nos quedamos con el snapshot y listo.
    const timeout = setTimeout(() => ctrl.abort(), 6000);

    fetch('/api/catalog', { signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d) => {
        const lista = normalizar(d.products || []);
        if (!vivo) return;
        // Los ajustes se aplican aunque el catálogo venga vacío: son independientes
        // de que haya productos publicados.
        setAjustes(normalizarAjustes(d.settings));
        if (lista.length) {
          setProductos(lista);
          setOrigen('api');
        }
      })
      .catch(() => {
        // Silencio a propósito: el snapshot ya está en pantalla y el visitante no
        // tiene nada que hacer con este error.
      })
      .finally(() => clearTimeout(timeout));

    return () => {
      vivo = false;
      clearTimeout(timeout);
      ctrl.abort();
    };
  }, []);

  return { productos, ajustes, origen };
}
